import TextInputIcon from "@redotech/redo-icons/text-input.svg";
import {
  InputPartType,
  InputValidation,
  Step as ModelStep,
  TextValidationSettings,
} from "@redotech/redo-model/return-flow";
import { inputFromModel } from "@redotech/redo-model/return-flow-step-from-model";
import {
  InputState,
  isInputStepValid,
} from "@redotech/redo-model/return-flow-step-valid";
import { BlockLayout } from "@redotech/redo-web/flowchart";
import { produce } from "immer";
import * as React from "react";
import { StepDownstream, StepType, StepTypeDetailsProps } from "../step";

export const INPUT = (
  Details: React.ComponentType<StepTypeDetailsProps<InputState>>,
): StepType<InputState, ModelStep.Input> => ({
  Details: (props: StepTypeDetailsProps<InputState>) =>
    React.createElement(Details, props),
  customTitle(state) {
    return state.customTitle;
  },
  title: "Input",
  valid(state) {
    return isInputStepValid(state).valid;
  },
  Icon: TextInputIcon,
  description(state) {
    return state.prompt;
  },
  downstream(state) {
    const result: StepDownstream[] = [];
    if (state.next !== undefined) {
      result.push({ id: state.next });
    }
    return result;
  },
  maxDownstream() {
    return 1;
  },
  layout() {
    return BlockLayout.FULL;
  },
  empty: {
    prompt: "",
    description: "",
    type: InputPartType.TEXTAREA,
    validation: InputValidation.OPTIONAL,
    next: undefined,
    exampleImage: undefined,
  },
  stepDeleted(state, stepId) {
    return produce(state, (state) => {
      if (state.next === stepId) {
        state.next = undefined;
      }
    });
  },
  updateConnection(state, handleIndex, targetStepId) {
    if (handleIndex !== 0) {
      throw new Error(`Invalid handle index ${handleIndex} for Input step`);
    }
    return produce(state, (draft) => {
      draft.next = targetStepId;
    });
  },
  removeConnection(state, targetStepId) {
    return produce(state, (draft) => {
      if (draft.next === targetStepId) {
        draft.next = undefined;
      }
    });
  },
  toModel(state, id) {
    const textValidationSettings: TextValidationSettings | undefined =
      state.type === InputPartType.TEXTAREA &&
      (state.minLength !== undefined ||
        state.maxLength !== undefined ||
        state.contentType !== undefined ||
        state.customRegex !== undefined ||
        state.useAdvancedValidation !== undefined)
        ? {
            minLength: state.minLength,
            maxLength: state.maxLength,
            contentType: state.contentType,
            customRegex: state.customRegex,
            customRegexErrorMessage: state.customRegexErrorMessage,
            useAdvancedValidation: state.useAdvancedValidation,
          }
        : undefined;

    return {
      customTitle: state.customTitle,
      type: ModelStep.INPUT,
      next: id(state.next!),
      parts: [
        {
          description: state.description,
          message: state.prompt,
          validation: state.validation,
          type: state.type!,
          exampleImage: state.exampleImage,
          textValidationSettings,
          aiVerification: state.aiVerification,
          aiVerificationStrictness: state.aiVerificationStrictness,
        },
      ],
    };
  },
  fromModel(model, id) {
    return inputFromModel(model, id);
  },
});

export { type InputState } from "@redotech/redo-model/return-flow-step-valid";
